import { WidgetEnv } from "@xapp/stentor-chat-widget";
import React, { FC, useMemo } from "react";
import { Provider } from "react-redux";
import { ChatWidgetProps } from ".";
import { useServerConfig } from "../../config";
import { MessageMiddleware, useStandardMiddleware } from "../../middlewares";
import { CONNECTION_STATUS_TYPE } from "../../store/ChatAction";
import { createChatStore } from "../../store/ChatStore";
import ChatWidget, { ChatWidgetMode } from "./ChatWidget";

export interface ChatWidgetContainerProps {
    readonly preChatFormEnabled?: boolean;
    readonly mode?: ChatWidgetMode;
    readonly config?: WidgetEnv;
    onConnectionStatusChange?(status: CONNECTION_STATUS_TYPE): void;
    /**
     * Optional async callback to load configuration.
     * If provided, the widget will wait for this to resolve before initializing.
     * This is useful for React Native apps where config may need to be fetched
     * asynchronously or where there's a delay in prop propagation.
     *
     * @returns Promise that resolves to the WidgetEnv config
     */
    getConfig?(): Promise<WidgetEnv>;
}

const ChatWidgetContainer: FC<ChatWidgetContainerProps> = props => {

    const messageMiddleware: MessageMiddleware = useStandardMiddleware();

    const connection = useServerConfig(props.config);

    const chatStore = useMemo(() => createChatStore({
        connection,
        userId: props.config?.userId,
        accessToken: props.config?.accessToken,
        attributes: props.config?.attributes,
        sessionExpiration: props.config?.sessionExpiration,
        enableErrorOverlay: props.config?.enableErrorOverlay
    }), [connection, props.config?.userId, props.config?.accessToken, props.config?.attributes, props.config?.sessionExpiration, props.config?.enableErrorOverlay]);

    if (props.config?.disabled) {
        return <></>;
    }

    const widgetProps: ChatWidgetProps = {
        ...props,
        messageMiddleware,
        getConfig: props.getConfig // Pass through getConfig callback
    }

    return (
        <Provider store={chatStore}>
            <ChatWidget
                {...widgetProps}
            />
        </Provider>
    );
}

export default ChatWidgetContainer;